import React, { useState } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { PersonFinal } from '@/api/entities';
import { Upload, CheckCircle, AlertCircle, Loader2 } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';

export default function EventsAttendedUploader() {
  const [file, setFile] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [results, setResults] = useState(null);

  const parseCSV = (text) => {
    const lines = text.split('\n').filter(line => line.trim());
    if (lines.length === 0) return [];
    
    const headers = lines[0].split(',').map(h => h.trim().toLowerCase().replace(/"/g, ''));
    const rows = [];
    
    for (let i = 1; i < lines.length; i++) {
      const values = lines[i].split(',').map(v => v.trim().replace(/"/g, ''));
      const row = {};
      headers.forEach((header, idx) => {
        row[header] = values[idx] || '';
      });
      rows.push(row);
    }
    
    return rows;
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      setFile(selectedFile);
      setResults(null);
    }
  };

  const handleUpload = async () => {
    if (!file) return;

    setIsProcessing(true);
    setResults(null);

    try {
      const text = await file.text();
      const rows = parseCSV(text);
      
      if (rows.length === 0) {
        setResults({ error: 'No data found in CSV' });
        setIsProcessing(false);
        return;
      }

      // Check for required columns
      const firstRow = rows[0];
      if (!firstRow.person_id && !firstRow.personid) {
        setResults({ error: 'CSV must have a person_id column' });
        setIsProcessing(false);
        return;
      }
      if (!firstRow.events_attended && !firstRow.eventsattended) {
        setResults({ error: 'CSV must have an events_attended column' });
        setIsProcessing(false);
        return;
      }

      // Fetch all people
      const allPeople = await PersonFinal.filter({});
      
      let updated = 0;
      let notFound = 0;
      const errors = [];

      for (const row of rows) {
        const personId = row.person_id || row.personid;
        const eventsRaw = row.events_attended || row.eventsattended;
        
        if (!personId || !eventsRaw) continue;

        // Parse events - support both semicolon and pipe delimiters
        const events = eventsRaw.split(/[;|]/).map(e => e.trim()).filter(Boolean);
        
        if (events.length === 0) continue;

        // Find person by person_id field or database id
        const person = allPeople.find(p => 
          p.person_id === personId || 
          p.id === personId ||
          p.person_id?.toLowerCase() === personId.toLowerCase()
        );

        if (!person) {
          notFound++;
          errors.push(`Person not found: ${personId}`);
          continue;
        }

        try {
          // Merge with existing events if any
          const existingEvents = person.events_attended || [];
          const mergedEvents = [...new Set([...existingEvents, ...events])];
          
          await PersonFinal.update(person.id, { events_attended: mergedEvents });
          updated++;
        } catch (err) {
          errors.push(`Failed to update ${personId}: ${err.message}`);
        }
      }

      setResults({
        success: true,
        updated,
        notFound,
        total: rows.length,
        errors: errors.slice(0, 10) // Show first 10 errors
      });
    } catch (error) {
      setResults({ error: error.message });
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">Events Attended Uploader</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-sm text-gray-600">
          Upload a CSV with <code className="bg-gray-100 px-1 rounded">person_id</code> and <code className="bg-gray-100 px-1 rounded">events_attended</code> columns.
          Multiple events can be separated by semicolons (;) or pipes (|).
        </p>
        
        <div className="flex items-center gap-3">
          <Input
            type="file"
            accept=".csv"
            onChange={handleFileChange}
            className="flex-1"
          />
          <Button
            onClick={handleUpload}
            disabled={!file || isProcessing}
          >
            {isProcessing ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Processing...
              </>
            ) : (
              <>
                <Upload className="w-4 h-4 mr-2" />
                Upload
              </>
            )}
          </Button>
        </div>

        {results && (
          <div className="space-y-2">
            {results.error ? (
              <Alert variant="destructive">
                <AlertCircle className="w-4 h-4" />
                <AlertDescription>{results.error}</AlertDescription>
              </Alert>
            ) : (
              <>
                <Alert className="border-green-200 bg-green-50">
                  <CheckCircle className="w-4 h-4 text-green-600" />
                  <AlertDescription className="text-green-800">
                    Updated {results.updated} of {results.total} records.
                    {results.notFound > 0 && ` (${results.notFound} not found)`}
                  </AlertDescription>
                </Alert>
                {results.errors && results.errors.length > 0 && (
                  <div className="text-sm text-red-600 space-y-1">
                    {results.errors.map((err, idx) => (
                      <p key={idx}>• {err}</p>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}